import { dayCellsForMonth } from '~/shared/dates';

const W = 1080;
const H = 1350;

const COLORS = {
  cream: '#F7F3EA',
  cream50: '#FCFAF5',
  cream200: '#EFE9DA',
  ink: '#15181F',
  inkMuted: '#5F5A52',
  lime: '#C8FF4A',
  border: '#E2DCD0',
} as const;

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export interface ImageGenParams {
  pollTitle: string;
  prefix: string;
  highlight: string;
  monthYear: { year: number; month: number };
  highlightDays: Set<string>;
  url: string;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

function drawMark(ctx: CanvasRenderingContext2D, x: number, y: number, size: number): void {
  const s = size / 64;
  ctx.save();
  ctx.translate(x, y);
  ctx.fillStyle = COLORS.ink;
  roundRect(ctx, 6 * s, 12 * s, 52 * s, 46 * s, 10 * s);
  ctx.fill();
  roundRect(ctx, 16 * s, 6 * s, 6 * s, 14 * s, 3 * s);
  ctx.fill();
  roundRect(ctx, 42 * s, 6 * s, 6 * s, 14 * s, 3 * s);
  ctx.fill();
  ctx.fillStyle = COLORS.lime;
  roundRect(ctx, 14 * s, 32 * s, 36 * s, 14 * s, 4 * s);
  ctx.fill();
  ctx.fillStyle = COLORS.cream;
  for (const dx of [18, 26, 34, 42]) {
    ctx.beginPath();
    ctx.arc(dx * s, 26 * s, 1.6 * s, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.strokeStyle = COLORS.ink;
  ctx.lineWidth = 3 * s;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(22 * s, 39 * s);
  ctx.lineTo(26 * s, 43 * s);
  ctx.lineTo(36 * s, 33 * s);
  ctx.stroke();
  ctx.restore();
}

function fitFontSize(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  desiredSize: number,
  weight: string,
  family: string,
  minSize = 36,
): number {
  let size = desiredSize;
  while (size > minSize) {
    ctx.font = `${weight} ${size}px ${family}`;
    if (ctx.measureText(text).width <= maxWidth) return size;
    size -= 4;
  }
  return minSize;
}

function drawCalendar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  params: ImageGenParams,
): number {
  const cells = dayCellsForMonth(params.monthYear.year, params.monthYear.month);
  const rows = cells.length / 7;

  const headerH = 80;
  const dowH = 36;
  const padding = 24;
  const cellW = (w - padding * 2) / 7;
  const cellH = cellW;
  const totalH = padding + headerH + dowH + cellH * rows + padding;

  ctx.fillStyle = COLORS.cream50;
  roundRect(ctx, x, y, w, totalH, 20);
  ctx.fill();
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  const innerX = x + padding;
  const innerY = y + padding;

  ctx.fillStyle = COLORS.ink;
  ctx.font = '600 36px "Bricolage Grotesque", system-ui, sans-serif';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillText(`${MONTHS[params.monthYear.month]} ${params.monthYear.year}`, innerX, innerY);

  const dowY = innerY + headerH;
  ctx.fillStyle = COLORS.inkMuted;
  ctx.font = '500 16px "Geist Mono", "SF Mono", ui-monospace, monospace';
  ctx.textAlign = 'center';
  for (let i = 0; i < 7; i += 1) {
    ctx.fillText(DOW[i]!, innerX + cellW / 2 + cellW * i, dowY);
  }

  const cellsY = dowY + dowH;
  const cellPad = 4;

  for (let i = 0; i < cells.length; i += 1) {
    const iso = cells[i];
    if (!iso) continue;
    const col = i % 7;
    const row = Math.floor(i / 7);
    const cx = innerX + col * cellW;
    const cy = cellsY + row * cellH;

    const highlighted = params.highlightDays.has(iso);

    if (highlighted) {
      ctx.fillStyle = COLORS.lime;
    } else {
      ctx.fillStyle = COLORS.cream;
    }
    roundRect(ctx, cx + cellPad, cy + cellPad, cellW - cellPad * 2, cellH - cellPad * 2, 10);
    ctx.fill();

    if (!highlighted) {
      ctx.strokeStyle = COLORS.border;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    const dayNum = Number(iso.slice(8, 10));
    ctx.fillStyle = COLORS.ink;
    ctx.font = `${highlighted ? 600 : 500} 22px "Geist", system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(dayNum), cx + cellW / 2, cy + cellH / 2);
  }

  return totalH;
}

export async function generateShareImage(params: ImageGenParams): Promise<Blob> {
  if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* ignore — still generate with whatever fonts are available */
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas context unavailable');

  ctx.fillStyle = COLORS.cream;
  ctx.fillRect(0, 0, W, H);

  drawMark(ctx, 60, 60, 56);
  ctx.fillStyle = COLORS.ink;
  ctx.font = '700 36px "Bricolage Grotesque", system-ui, sans-serif';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText('whomfree', 132, 92);

  const margin = 60;
  const contentW = W - margin * 2;

  const prefixSize = fitFontSize(
    ctx,
    params.prefix,
    contentW,
    72,
    '500',
    '"Bricolage Grotesque", system-ui, sans-serif',
    36,
  );
  ctx.fillStyle = COLORS.ink;
  ctx.font = `500 ${prefixSize}px "Bricolage Grotesque", system-ui, sans-serif`;
  ctx.textBaseline = 'top';
  ctx.fillText(params.prefix, margin, 200);

  const highlightText = `${params.highlight}!`;
  const highlightSize = fitFontSize(
    ctx,
    highlightText,
    contentW - 24,
    96,
    '700',
    '"Bricolage Grotesque", system-ui, sans-serif',
    48,
  );
  ctx.font = `700 ${highlightSize}px "Bricolage Grotesque", system-ui, sans-serif`;
  const highlightWidth = ctx.measureText(highlightText).width;
  const highlightY = 200 + prefixSize + 16;
  const highlightPad = 14;
  ctx.fillStyle = COLORS.lime;
  roundRect(
    ctx,
    margin - highlightPad / 2,
    highlightY + highlightSize * 0.18,
    highlightWidth + highlightPad,
    highlightSize * 0.85,
    10,
  );
  ctx.fill();
  ctx.fillStyle = COLORS.ink;
  ctx.fillText(highlightText, margin, highlightY);

  // Constrain calendar width so cells stay reasonably small + leave room
  // for the title + URL underneath without overlap.
  const calMaxW = Math.min(contentW, 760);
  const calX = margin + (contentW - calMaxW) / 2;
  const calY = highlightY + highlightSize + 60;
  const calH = drawCalendar(ctx, calX, calY, calMaxW, params);
  const calBottom = calY + calH;

  const titleSize = 26;
  const urlSize = 22;
  const titleGap = 48;
  const urlGap = 12;
  const bottomMargin = 60;
  const blockH = titleSize + urlGap + urlSize;
  // Anchor the title+URL block to the bottom margin, but ensure at least
  // `titleGap` of breathing room beneath the calendar.
  const titleY = Math.max(calBottom + titleGap, H - bottomMargin - blockH);

  ctx.fillStyle = COLORS.inkMuted;
  ctx.font = `500 ${titleSize}px "Geist", system-ui, sans-serif`;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillText(params.pollTitle, margin, titleY);

  ctx.fillStyle = COLORS.ink;
  ctx.font = `500 ${urlSize}px "Geist Mono", "SF Mono", ui-monospace, monospace`;
  ctx.fillText(params.url, margin, titleY + titleSize + urlGap);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('canvas toBlob failed'));
    }, 'image/png');
  });
}

export type ShareImageOutcome = 'shared' | 'cancelled' | 'downloaded';

export async function shareOrDownloadImage(
  blob: Blob,
  filename: string,
): Promise<ShareImageOutcome> {
  const file = new File([blob], filename, { type: 'image/png' });
  if (
    typeof navigator !== 'undefined' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [file] }) &&
    typeof navigator.share === 'function'
  ) {
    try {
      await navigator.share({ files: [file] });
      return 'shared';
    } catch {
      return 'cancelled';
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return 'downloaded';
}
